import React, { useEffect, useState } from "react";
import { IdentificationOpenType, IdentificationRequestQueries, IdentificationResponseData } from "maven-lib/dist/dto";
import { useRequest } from "@coralblack/flax";
import { hasOwnProperty } from "maven-lib/dist/utils/misc";
import { HbButton } from "@maven-surface/components/base/HbButton";
import { COMMON_LANG } from "@maven-msg/lang/common";
import { MvnMessage } from "@maven-msg/lib/Message";

interface IdentificationFrameButtonByDanalProps {
  buttonId: string;
  targetFrameId: string;
  frameContainer: React.RefObject<HTMLDivElement>;
  callbackUrl: string;
  callbackFunction: (responseData: IdentificationResponseData) => void;
  className?: string;
  buttonText?: string;
  onPrepareIdentification?: () => void;
}

function getIdentificationKeyToDanalNames() {
  const map = new Map<string, string>();

  map.set("tradeId", "TID");
  map.set("backUrl", "BackURL");
  map.set("bgColor", "BgColor");
  map.set("isCharSet", "IsCharSet");
  map.set("isCarrier", "IsCarrier");
  map.set("isDestinationAddress", "IsDstAddr");
  map.set("byButtonId", "ByButtonId");
  map.set("byOriginUrl", "ByOriginURL");
  map.set("byOpenType", "ByOpenType");

  return map;
}

function createIdentificationFrame(targetFrameId: string, frameContainer: React.RefObject<HTMLDivElement>) {
  const iframe = document.createElement("iframe");

  iframe.id = targetFrameId;
  iframe.name = targetFrameId;
  iframe.frameBorder = "0";
  iframe.width = "100%";
  iframe.height = `${(window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight) - 60}`;

  frameContainer.current?.appendChild(iframe);
  return iframe;
}

function createIdentificationForm(
  queries: IdentificationRequestQueries,
  targetFrameId: string,
  frameContainer: React.RefObject<HTMLDivElement>
) {
  const identificationKeyNames = getIdentificationKeyToDanalNames();
  const frm = document.createElement("form");

  for (const key in queries) {
    const input = document.createElement("input");

    input.type = "hidden";
    input.name = identificationKeyNames.get(key) || "";
    input.value = queries[key] || "";
    frm.appendChild(input);
  }

  frm.name = targetFrameId;
  frm.method = "post";
  frm.action = "https://wauth.teledit.com/Danal/WebAuth/Web/Start.php";
  frm.target = targetFrameId;
  frameContainer.current?.appendChild(frm);

  return frm;
}

function openWindow(queries: IdentificationRequestQueries, targetFrameId: string, frameContainer: React.RefObject<HTMLDivElement>) {
  const frm = createIdentificationForm(queries, targetFrameId, frameContainer);
  const frame = createIdentificationFrame(targetFrameId, frameContainer);

  frm.submit();
  return { frm, frame };
}

export function IdentificationFrameButtonByDanal(props: IdentificationFrameButtonByDanalProps) {
  const { buttonId, targetFrameId, frameContainer, callbackUrl, callbackFunction, className, buttonText, onPrepareIdentification } = props;
  const [disabled, setDisabled] = useState<boolean>(false);

  const { request: verifyDanalMember } = useRequest(
    {
      method: "POST",
      url: `${process.env.REACT_APP_SANTA_BE_URL}/identification/prepare/danal`,
      data: { callbackUrl: `${process.env.REACT_APP_SANTA_BE_URL}/identification-redirect/confirm/danal` },
    },
    {
      success: (responseData) => {
        openWindow(
          {
            tradeId: responseData.tradeId || "",
            isCharSet: "UTF-8",
            byButtonId: buttonId,
            byOriginUrl: callbackUrl,
            byOpenType: IdentificationOpenType.Iframe,
          },
          targetFrameId,
          frameContainer
        );
        onPrepareIdentification && onPrepareIdentification();
        setDisabled(true);
      },
      error: (error) => MvnMessage("Request failed identity verification", { message: error?.message || "An error has occurred." }),
    }
  );

  useEffect(() => {
    const messageEventFunction = (event: MessageEvent) => {
      const responseData = event?.data;
      const form = frameContainer.current?.querySelector("form");
      const frame = frameContainer.current?.querySelector("iframe");

      if (
        responseData &&
        responseData.buttonId === buttonId &&
        hasOwnProperty(responseData, "result") &&
        hasOwnProperty(responseData, "returnCode") &&
        hasOwnProperty(responseData, "returnMsg") &&
        hasOwnProperty(responseData, "tradeId")
      ) {
        callbackFunction(responseData);

        form && frameContainer.current?.removeChild(form);
        frame && frameContainer.current?.removeChild(frame);
        setDisabled(false);
      }
    };

    window.addEventListener("message", messageEventFunction);
    return () => {
      window.removeEventListener("message", messageEventFunction);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <HbButton
      className={className}
      disabled={disabled}
      text={buttonText || MvnMessage(COMMON_LANG.REQUEST)}
      onClick={() => {
        verifyDanalMember();
      }}
    />
  );
}
